import * as React from 'react';
import { and, filter, concat, compose, converge, reduce, remove, pathOr } from 'ramda';
import styled from 'styled-components';
import { PlusIcon } from '../Icons';
import { Colors } from '@prezi/dragon';
import Tag from './Tag';
import TagInput from './TagInput';
import TagAddButton from './TagAddButton';

type Tag = {
    text: string;
    disabled?: boolean;
};

type TagAndText = [string[], Tag[]];

type Props = {
    list?: Array<string | Tag>;
    setList: (tags: string[]) => void;
    placeholders?: string[];
    maxItemWidth?: number;
};

const Wrap = styled.div`
    display: flex;
    flex-wrap: wrap;
    margin: 0 -4px;
`;

const UserTag = styled(Tag)<{ disabled?: boolean }>`
    pointer-events: ${p => (p.disabled ? 'none' : 'all')};
`;

const isNonDuplicate = (list: string[]) => (value: string) => !list.find(text => text === value);

const isNonEmpty = (value: string) => value !== '';

const validate = (list: string[]) => converge(and, [isNonEmpty, isNonDuplicate(list)]);

// transforms tag to lowercase and splits it by whitespace or commas
const tagify = (value: string) => value.toLowerCase().split(/[ ,]+/);

const updateTags = (value: string, list: string[]) => compose(concat(list), filter(validate(list)), tagify)(value);

const toTextTagArrays = (acc: TagAndText, tag: Tag | string): TagAndText => [
    [...acc[0], pathOr(tag, ['text'], tag) as string],
    [...acc[1], { text: pathOr(tag, ['text'], tag), disabled: pathOr(false, ['disabled'], tag) } as Tag],
];

const TagsEditor = ({ list = [], placeholders = [], setList, maxItemWidth }: Props) => {
    const [isAdding, setAdding] = React.useState<boolean>(false);
    const [textlist, taglist] = reduce(toTextTagArrays, [[], []], list);
    const hasPlaceholders = !isAdding && taglist.length === 0;

    return (
        <Wrap>
            {taglist.map(({ text, disabled }: Tag, i: number) => (
                <UserTag
                    key={i}
                    label={text}
                    disabled={disabled}
                    maxWidth={maxItemWidth}
                    onDelete={() => !disabled && setList(remove(i, 1, textlist))}
                />
            ))}
            {hasPlaceholders &&
                placeholders.map((text, i) => (
                    <Tag key={i} label={text} disabled={true} maxWidth={maxItemWidth} onClick={() => setAdding(true)} />
                ))}
            {isAdding && (
                <TagInput
                    onSave={(value, openNewTagAfterSave = false) => {
                        if (value) {
                            setList(updateTags(value, textlist));
                        }
                        setAdding(openNewTagAfterSave);
                    }}
                />
            )}
            {!isAdding && (
                <TagAddButton onClick={() => setAdding(true)}>
                    <PlusIcon width={24} height={24} fill={Colors.neutral400} />
                </TagAddButton>
            )}
        </Wrap>
    );
};

export default TagsEditor;
